import type { Item, Addon } from "../../../../api/menu";
import { applyBillPercentToPrice } from "../billDiscount";

interface OrderItem {
  itemId: number;
  quantity: number;
  modifiers: number[];
  addons: Record<number, Record<number, number>>; // addonId -> { itemId -> количество }
  comment?: string;
}

/** Ред от „Преглед на поръчката" — един или няколко слети реда от кошницата. */
export interface OrderRow {
  /** Индексите в `orderItems`, които редът покрива (винаги един без обобщаване). */
  indices: number[];
  /** Представителят на групата — всички в нея са с еднакви добавки/модификатори/коментар. */
  orderItem: OrderItem;
  /** Сборното количество на групата. */
  quantity: number;
}

/**
 * Ключ за обобщаване. Слива се САМО напълно еднакъв ред: същият продукт с
 * абсолютно същите добавки (и техните количества), същите модификатори и
 * същия коментар — разликата в което и да е от трите прави отделен ред.
 */
const orderRowKey = (orderItem: OrderItem): string =>
  JSON.stringify([
    orderItem.itemId,
    (orderItem.comment ?? "").trim(),
    [...orderItem.modifiers].sort((a, b) => a - b),
    Object.entries(orderItem.addons)
      .map(([addonId, quantityByItemId]) => [
        Number(addonId),
        Object.entries(quantityByItemId)
          .map(([itemId, quantity]) => [Number(itemId), quantity])
          .sort((a, b) => a[0] - b[0]),
      ])
      .sort((a, b) => (a[0] as number) - (b[0] as number)),
  ]);

/**
 * Превръща кошницата в редовете за показване. Без обобщаване всеки запис е
 * свой ред (както досега); с обобщаване еднаквите записи стават един ред със
 * сборно количество, но продължават да сочат оригиналните си индекси, за да
 * работят степерите върху истинската кошница.
 */
export const buildOrderRows = (
  orderItems: OrderItem[],
  summarized: boolean,
): OrderRow[] => {
  if (!summarized) {
    return orderItems.map((orderItem, index) => ({
      indices: [index],
      orderItem,
      quantity: orderItem.quantity,
    }));
  }

  const rowByKey = new Map<string, OrderRow>();
  const rows: OrderRow[] = [];
  orderItems.forEach((orderItem, index) => {
    const key = orderRowKey(orderItem);
    const existing = rowByKey.get(key);
    if (existing) {
      existing.indices.push(index);
      existing.quantity += orderItem.quantity;
      return;
    }
    const row: OrderRow = {
      indices: [index],
      orderItem,
      quantity: orderItem.quantity,
    };
    rowByKey.set(key, row);
    rows.push(row);
  });
  return rows;
};

export const calculateOrderTotal = (
  orderItems: OrderItem[],
  allItems: Item[],
  allAddons: Addon[]
): number => {
  return orderItems.reduce((total, orderItem) => {
    const item = allItems.find((i) => i.id === orderItem.itemId);
    if (!item) return total;

    let itemTotal = item.price * orderItem.quantity;

    Object.entries(orderItem.addons).forEach(([addonId, qtyMap]) => {
      const addon = allAddons.find((a) => a.id === Number(addonId));
      // Тип "1" (обикновена) е с АБСОЛЮТНО количество (броячът от модала);
      // тип "2"/"3" са по 1 на продукт → × количеството на продукта.
      const absolute = String(addon?.type) === "1";
      Object.entries(qtyMap).forEach(([itemId, addonQty]) => {
        const addonItemData = addon?.items.find(
          (ai) => ai.itemId === Number(itemId)
        );
        // Цена на добавката: променената за групата (addonItemPrice), а ако
        // няма такава — основната цена на асортимента. Същото правило като
        // в реда на прегледа и в payload-а към сървъра (handleConfirmOrder).
        const addonItemPrice =
          addonItemData?.addonItemPrice ?? addonItemData?.price ?? 0;
        itemTotal +=
          addonItemPrice * (absolute ? addonQty : addonQty * orderItem.quantity);
      });
    });

    return total + itemTotal;
  }, 0);
};

export interface OrderPricing {
  /** Сборът по основни цени (същото като calculateOrderTotal). */
  baseTotal: number;
  /** Сборът след процента по сметката (клиент с отстъпка/надбавка). */
  finalTotal: number;
  /** finalTotal − baseTotal: отрицателна при отстъпка, положителна при надбавка. */
  adjustment: number;
}

/**
 * Общо на кошницата + вариант с приложения процент по сметката. Огледално на
 * BE (bills/service.py, добавяне към сметка с активна отстъпка): всяка
 * коригирана порция (продукт/добавка) се закръгля отделно до 2 знака, а
 * редовете със заключена цена (lockedPrice) не се коригират — включително
 * добавките им.
 */
export const calculateOrderPricing = (
  orderItems: OrderItem[],
  allItems: Item[],
  allAddons: Addon[],
  billPercent: number,
): OrderPricing => {
  const roundMoney = (value: number) => Math.round(value * 100) / 100;

  let baseTotal = 0;
  let finalTotal = 0;

  for (const orderItem of orderItems) {
    const item = allItems.find((i) => i.id === orderItem.itemId);
    if (!item) continue;
    const lineDiscounted = billPercent !== 0 && !item.lockedPrice;

    const productPortion = item.price * orderItem.quantity;
    baseTotal += productPortion;
    finalTotal += lineDiscounted
      ? roundMoney(applyBillPercentToPrice(productPortion, billPercent))
      : productPortion;

    Object.entries(orderItem.addons).forEach(([addonId, qtyMap]) => {
      const addon = allAddons.find((a) => a.id === Number(addonId));
      // Тип "1" (обикновена) е с АБСОЛЮТНО количество — виж calculateOrderTotal
      const absolute = String(addon?.type) === "1";
      Object.entries(qtyMap).forEach(([itemId, addonQty]) => {
        const addonItemData = addon?.items.find(
          (ai) => ai.itemId === Number(itemId),
        );
        const addonItemPrice =
          addonItemData?.addonItemPrice ?? addonItemData?.price ?? 0;
        const addonPortion =
          addonItemPrice *
          (absolute ? addonQty : addonQty * orderItem.quantity);
        baseTotal += addonPortion;
        finalTotal += lineDiscounted
          ? roundMoney(applyBillPercentToPrice(addonPortion, billPercent))
          : addonPortion;
      });
    });
  }

  return { baseTotal, finalTotal, adjustment: finalTotal - baseTotal };
};
